import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './styles/App.css';

// Auth
import Login from './pages/Login';
import Register from './pages/Register';

// Pages — Home
import Dashboard from './pages/Dashboard';

// Pages — Family
import Grocery from './pages/Grocery';
import Todo from './pages/Todo';
import Appointments from './pages/Appointments';
import WishList from './pages/WishList';
import Household from './pages/Household';

// Pages — Financial Management
import Subscriptions from './pages/Subscriptions';
import Bills from './pages/Bills';
import Debts from './pages/Debts';
import Assets from './pages/Assets';
import AppointmentCalendar from './pages/AppointmentCalendar';

// Pages — Resources
import KnowledgeBase from './pages/KnowledgeBase';
import Recipes from './pages/Recipes';
import WebLinks from './pages/WebLinks';
import PasswordManager from './pages/PasswordManager';

// Pages — Admin
import HouseholdManagement from './pages/HouseholdManagement';
import KnowledgeBaseAdmin from './pages/KnowledgeBaseAdmin';
import SuperAdmin from './pages/SuperAdmin';
import Account from './pages/Account';
import ForcedTwoFASetup from './pages/ForcedTwoFASetup';

// Components
import Sidebar from './components/Sidebar';
import TopTabBar, { ROUTE_TO_TAB } from './components/TopTabBar';
import BottomNav from './components/BottomNav';
import MobileHeader from './components/MobileHeader';

function AppShell({ isAuthenticated, user, onLogin, onLogout, onUpdateUser, sessionExpired }) {
  const location = useLocation();
  const activeTab = ROUTE_TO_TAB[location.pathname] || 'family';

  const auth = (el) => (isAuthenticated ? el : <Navigate to="/login" replace />);

  // Block access until 2FA is set up
  if (isAuthenticated && user?.totp_enabled === false) {
    return <ForcedTwoFASetup onComplete={() => onUpdateUser({ totp_enabled: true })} onLogout={onLogout} />;
  }

  return (
    <div className="App">
      {isAuthenticated && <div className="ambient-bg"></div>}
      {isAuthenticated && <Sidebar user={user} onLogout={onLogout} activeTab={activeTab} />}
      {isAuthenticated && <MobileHeader user={user} onLogout={onLogout} />}

      <div className={isAuthenticated ? 'main-content' : ''}>
        {isAuthenticated && <TopTabBar />}
        <main>
          <Routes>
            <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login onLogin={onLogin} sessionExpired={sessionExpired} />} />
            <Route path="/register" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Register onRegister={onLogin} />} />

            {/* Home */}
            <Route path="/dashboard" element={auth(<Dashboard user={user} />)} />

            {/* Family */}
            <Route path="/grocery" element={auth(<Grocery />)} />
            <Route path="/todo" element={auth(<Todo />)} />
            <Route path="/wishlist" element={auth(<WishList />)} />
            <Route path="/appointments" element={auth(<Appointments />)} />
            <Route path="/household" element={auth(<Household />)} />

            {/* Financial Management */}
            <Route path="/subscriptions" element={auth(<Subscriptions />)} />
            <Route path="/bills" element={auth(<Bills />)} />
            <Route path="/debts" element={auth(<Debts />)} />
            <Route path="/assets" element={auth(<Assets />)} />
            <Route path="/appointments/calendar" element={auth(<AppointmentCalendar />)} />

            {/* Resources */}
            <Route path="/knowledge-base" element={auth(<KnowledgeBase />)} />
            <Route path="/recipes" element={auth(<Recipes />)} />
            <Route path="/web-links" element={auth(<WebLinks />)} />
            <Route path="/passwords" element={auth(<PasswordManager />)} />

            {/* Admin */}
            <Route path="/admin/household" element={auth(<HouseholdManagement />)} />
            <Route path="/admin/knowledge-categories" element={auth(<KnowledgeBaseAdmin />)} />
            <Route path="/admin/super" element={user?.is_super_admin ? auth(<SuperAdmin />) : <Navigate to="/dashboard" replace />} />

            <Route path="/account" element={auth(<Account user={user} />)} />

            <Route path="/" element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />} />
          </Routes>
        </main>
      </div>

      {isAuthenticated && <BottomNav user={user} />}
    </div>
  );
}

const SESSION_DURATION = 4 * 60 * 60 * 1000; // 4 hours in ms

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const handleLogout = (expired = false) => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('loginTime');
    setIsAuthenticated(false);
    setUser(null);
    if (expired) setSessionExpired(true);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const loginTime = localStorage.getItem('loginTime');

    if (token && userData && loginTime) {
      const elapsed = Date.now() - parseInt(loginTime, 10);
      if (elapsed >= SESSION_DURATION) {
        handleLogout(true);
      } else {
        setIsAuthenticated(true);
        setUser(JSON.parse(userData));
      }
    }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Timer to auto-logout when session expires
  useEffect(() => {
    if (!isAuthenticated) return;
    const loginTime = parseInt(localStorage.getItem('loginTime') || Date.now(), 10);
    const remaining = SESSION_DURATION - (Date.now() - loginTime);
    if (remaining <= 0) { handleLogout(true); return; }
    const timer = setTimeout(() => handleLogout(true), remaining);
    return () => clearTimeout(timer);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('loginTime', Date.now().toString());
    setIsAuthenticated(true);
    setUser(userData);
    setSessionExpired(false);
  };

  const handleUpdateUser = (patch) => {
    const updated = { ...user, ...patch };
    localStorage.setItem('user', JSON.stringify(updated));
    setUser(updated);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <Router>
      <AppShell
        isAuthenticated={isAuthenticated}
        user={user}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onUpdateUser={handleUpdateUser}
        sessionExpired={sessionExpired}
      />
    </Router>
  );
}

export default App;
